import { useState } from 'react';
import {
  Star,
  Shield,
  Award,
  CheckCircle,
  Clock,
  MapPin,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Calendar,
  Info
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FadeIn, SlideIn } from '@/components/ui/AnimatedComponents';

// Helper function to convert time slot codes to friendly names
const getTimeSlotLabel = (timeSlot: string): string => {
  const timeSlotMap: Record<string, string> = {
    'morning': 'Morning (Before 12 PM)',
    'afternoon': 'Afternoon (12 PM - 5 PM)',
    'evening': 'Evening (After 5 PM)',
    'after_11am': 'After 11 AM',
    'before_2pm': 'Before 2 PM',
    'flexible': 'Flexible (Any time)'
  };
  return timeSlotMap[timeSlot] || timeSlot;
};

interface QuoteComparisonProps {
  quotes: any[];
  onAcceptQuote: (quoteId: string) => void;
  onRejectQuote: (quoteId: string) => void;
  onMessageProvider: (providerId: string, providerName: string) => void;
  acceptingQuoteId?: string | null;
  rejectingQuoteId?: string | null;
}

export function QuoteComparison({
  quotes,
  onAcceptQuote,
  onRejectQuote,
  onMessageProvider,
  acceptingQuoteId,
  rejectingQuoteId
}: QuoteComparisonProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedQuotes, setSelectedQuotes] = useState<Set<string>>(new Set(quotes.map(q => q.id)));

  const toggleRow = (rowId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const toggleQuoteSelection = (quoteId: string) => {
    setSelectedQuotes(prev => {
      const next = new Set(prev);
      if (next.has(quoteId)) {
        next.delete(quoteId);
      } else {
        next.add(quoteId);
      }
      return next;
    });
  };

  // Filter to only show selected quotes
  const visibleQuotes = quotes.filter(q => selectedQuotes.has(q.id));

  if (visibleQuotes.length === 0) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <Info className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No quotes selected for comparison</p>
          <p className="text-sm text-gray-500 mt-1">Select at least one quote above</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with quote selection */}
      <Card padding="md">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Compare Quotes</h3>
            <p className="text-sm text-gray-600 mt-1">
              {selectedQuotes.size} of {quotes.length} selected
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quotes.map((quote) => (
            <label
              key={quote.id}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-all min-h-touch ${
                selectedQuotes.has(quote.id)
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-200'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedQuotes.has(quote.id)}
                onChange={() => toggleQuoteSelection(quote.id)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 min-h-[20px] min-w-[20px]"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate">
                  {quote.provider?.business_name || 'Provider'}
                </div>
                <div className="text-xs text-gray-500">€{quote.amount.toFixed(2)}</div>
              </div>
            </label>
          ))}
        </div>
      </Card>

      {/* Card-based comparison view (mobile-friendly) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {visibleQuotes.map((quote: any, index: number) => (
          <FadeIn key={quote.id} delay={index * 100}>
            <Card padding="none" className="overflow-hidden h-full">
              {/* Header */}
              <div className="p-4 sm:p-6 bg-gradient-to-br from-primary-50 to-white border-b border-gray-100">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-lg">
                    {quote.provider?.business_name?.charAt(0) || 'P'}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {quote.provider?.business_name || 'Provider'}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center">
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                        <span className="ml-1 text-sm font-medium text-gray-700">
                          {quote.provider?.rating?.toFixed(1) || '5.0'}
                        </span>
                      </div>
                      {quote.provider?.verified && (
                        <Badge variant="verified" size="sm">
                          <Shield className="w-3 h-3" /> Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary-600">
                    €{quote.amount.toFixed(2)}
                  </div>
                  {quote.status === 'accepted' && (
                    <Badge variant="success" size="sm" className="mt-1">
                      Accepted
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="p-4 sm:p-6 space-y-4">
              {/* Description */}
              {quote.description && (
                <div className="p-3 bg-gray-50 rounded-lg border-l-4 border-primary-300">
                  <p className="text-sm text-gray-700 italic">"{quote.description}"</p>
                </div>
              )}

              {/* Key Info Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {quote.estimated_duration && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500">Duration</div>
                      <div className="font-medium text-gray-900">{quote.estimated_duration}</div>
                    </div>
                  </div>
                )}
                {quote.provider?.location && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500">Location</div>
                      <div className="font-medium text-gray-900">{quote.provider.location}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Submitted</div>
                    <div className="font-medium text-gray-900">
                      {new Date(quote.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                {quote.provider?.years_of_experience > 0 && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Award className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500">Experience</div>
                      <div className="font-medium text-gray-900">
                        {quote.provider.years_of_experience} years
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Expandable Details */}
              {(quote.available_date_slots?.length > 0 || quote.provider?.specializations?.length > 0) && (
                <button
                  onClick={() => toggleRow(quote.id)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700 min-h-touch"
                >
                  <span>View More Details</span>
                  {expandedRows.has(quote.id) ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              )}

              {expandedRows.has(quote.id) && (
                <div className="space-y-3 pt-2 border-t border-gray-100">
                  {/* Available Slots */}
                  {quote.available_date_slots?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Available Dates
                      </div>
                      <div className="space-y-2">
                        {quote.available_date_slots.map((slot: any, idx: number) => (
                          <div key={idx} className="flex items-start gap-2 text-sm bg-blue-50 p-2 rounded border border-blue-200">
                            <Calendar className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900">
                                {new Date(slot.date).toLocaleDateString('en-IE', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </div>
                              <div className="text-xs text-gray-600">
                                {slot.time_slot ? getTimeSlotLabel(slot.time_slot) : `${slot.start_time} - ${slot.end_time}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Specializations */}
                  {quote.provider?.specializations?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Specializations
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {quote.provider.specializations.map((spec: string, idx: number) => (
                          <Badge key={idx} variant="neutral" size="sm">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Insurance */}
                  {quote.provider?.has_insurance && (
                    <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 p-2 rounded">
                      <Shield className="w-4 h-4" />
                      <span>Insured Professional</span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              {quote.status === 'pending' && (
                <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-100">
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={() => onAcceptQuote(quote.id)}
                    disabled={acceptingQuoteId === quote.id || rejectingQuoteId !== null}
                    leftIcon={<CheckCircle className="w-4 h-4" />}
                  >
                    {acceptingQuoteId === quote.id ? 'Accepting...' : 'Accept'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onRejectQuote(quote.id)}
                    disabled={rejectingQuoteId === quote.id || acceptingQuoteId !== null}
                  >
                    {rejectingQuoteId === quote.id ? 'Rejecting...' : 'Decline'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      onMessageProvider(
                        quote.provider?.id || quote.provider_id,
                        quote.provider?.business_name || 'Provider'
                      )
                    }
                    leftIcon={<MessageCircle className="w-4 h-4" />}
                  >
                    Message
                  </Button>
                </div>
              )}
            </div>
          </Card>
          </FadeIn>
        ))}
      </div>
    </div>
  );
}
